import { Collapse, Flex, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { PipelineStepId, PipelineStepState } from '../../types';
import {
  buildFriendlySummary,
  formatFriendlySummary,
} from '../../utils/step-display';
import { PipelineJsonPanel } from '../PipelineJsonPanel/PipelineJsonPanel';
import { PipelineStreamFeed } from '../PipelineStreamFeed/PipelineStreamFeed';

const { Text, Title } = Typography;
const { Dragger } = Upload;

type PipelineResultSectionProps = {
  activeStepId: PipelineStepId;
  activeStep: PipelineStepState;
  exampleInput: unknown;
  exampleOutput: unknown;
  isEditingDisabled?: boolean;
  isUploadDisabled?: boolean;
  onStepInputFilesSelected: (files: File[]) => void;
  onUpdateInput: (value: unknown) => void;
  onUpdateOutput: (value: unknown) => void;
  onCopyInput: () => void;
  onCopyOutput: () => void;
  onDownloadInput: () => void;
  onDownloadOutput: () => void;
  onCopyExampleInput: () => void;
  onCopyExampleOutput: () => void;
  onDownloadExampleInput: () => void;
  onDownloadExampleOutput: () => void;
};

export function PipelineResultSection({
  activeStepId,
  activeStep,
  exampleInput,
  exampleOutput,
  isEditingDisabled = false,
  isUploadDisabled = false,
  onStepInputFilesSelected,
  onUpdateInput,
  onUpdateOutput,
  onCopyInput,
  onCopyOutput,
  onDownloadInput,
  onDownloadOutput,
  onCopyExampleInput,
  onCopyExampleOutput,
  onDownloadExampleInput,
  onDownloadExampleOutput,
}: PipelineResultSectionProps) {
  const { t } = useTranslation('pipelineDebug');
  const headline = formatFriendlySummary(
    buildFriendlySummary(activeStepId, activeStep),
    t,
  );

  return (
    <Flex vertical gap={12}>
      <div
        className={`pipeline-debug-result-highlight pipeline-debug-result-highlight--${activeStepId}`}
      >
        <Text type="secondary">{t('result.currentStep')}</Text>
        <Title level={4} style={{ margin: '4px 0 0' }}>
          {t(`steps.${activeStepId}.title`)} ·{' '}
          {t(`steps.${activeStepId}.subtitle`)}
        </Title>
        <Text className="pipeline-debug-result-headline">{headline}</Text>
      </div>

      <PipelineStreamFeed activeStep={activeStep} />

      <Dragger
        multiple
        accept=".json,application/json"
        className="pipeline-debug-upload pipeline-debug-upload--step"
        beforeUpload={(file) => {
          onStepInputFilesSelected([file]);
          return false;
        }}
        showUploadList={false}
        disabled={isUploadDisabled || isEditingDisabled}
      >
        <Flex vertical align="center" gap={6} style={{ padding: '4px 0' }}>
          <InboxOutlined style={{ fontSize: 28, color: '#1E6DF6' }} />
          <Text strong style={{ fontSize: 14 }}>
            {t('stepUpload.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('stepUpload.hint')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(`steps.${activeStepId}.inputHint`)}
          </Text>
        </Flex>
      </Dragger>

      <Collapse
        bordered={false}
        className="pipeline-debug-collapse"
        defaultActiveKey={['technical']}
        items={[
          {
            key: 'technical',
            label: t('panels.showTechnical'),
            children: (
              <Flex vertical gap={16}>
                <PipelineJsonPanel
                  title={t('panels.currentInput')}
                  hint={t('panels.editInputHint')}
                  value={activeStep.input}
                  emptyText={t('panels.noInputYet')}
                  editable
                  disabled={isEditingDisabled}
                  onChange={onUpdateInput}
                  onCopy={onCopyInput}
                  onDownload={onDownloadInput}
                />
                <PipelineJsonPanel
                  title={t('panels.currentOutput')}
                  hint={t('panels.editOutputHint')}
                  value={activeStep.output}
                  emptyText={t('panels.noOutputYet')}
                  editable
                  disabled={isEditingDisabled}
                  onChange={onUpdateOutput}
                  onCopy={onCopyOutput}
                  onDownload={onDownloadOutput}
                />
              </Flex>
            ),
          },
          {
            key: 'reference',
            label: t('panels.showReference'),
            children: (
              <Flex vertical gap={16}>
                <PipelineJsonPanel
                  title={t('panels.exampleInput')}
                  hint={t(`steps.${activeStepId}.inputHint`)}
                  value={exampleInput}
                  onCopy={onCopyExampleInput}
                  onDownload={onDownloadExampleInput}
                />
                <PipelineJsonPanel
                  title={t('panels.exampleOutput')}
                  hint={t(`steps.${activeStepId}.outputHint`)}
                  value={exampleOutput}
                  onCopy={onCopyExampleOutput}
                  onDownload={onDownloadExampleOutput}
                />
              </Flex>
            ),
          },
        ]}
      />
    </Flex>
  );
}
